import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";

const statusColor = (status) => {
  switch (status) {
    case "new":
      return "info";
    case "in_progress":
      return "warning";
    case "scheduled":
      return "success";
    case "closed":
      return "default";
    default:
      return "default";
  }
};

const TuitionRequests = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [weekdays, setWeekdays] = useState([]);

  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    assigned_tutor_id: "",
    scheduled_date: "",
    scheduled_time: "",
    scheduled_weekday_id: "",
    duration_minutes: "",
    status: "new",
  });

  const canOpenDialog = useMemo(() => !!selected, [selected]);

  const fetchRequests = async () => {
    try {
      const res = await api.get("admin/tuition-requests");
      if (res.data?.success) setRequests(res.data.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load tuition requests.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTutors = async () => {
    try {
      const res = await api.get("admin/tutors", {
        params: { page: 1, per_page: 500 },
      });
      if (res.data?.success) setTutors(res.data.data?.data || []);
    } catch (e) {
      // Not fatal: admin can still save with empty tutor if you allow it.
      console.error("Failed to load tutors for tuition assignment:", e);
    }
  };

  const fetchWeekdays = async () => {
    try {
      const res = await api.get("admin/master-form/weekdays", {
        params: { per_page: 100 },
      });
      const data = res.data?.data?.data || res.data?.data || [];
      setWeekdays(data);
    } catch (e) {
      console.error("Failed to load weekdays for tuition scheduling:", e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([fetchRequests(), fetchTutors(), fetchWeekdays()]);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRequest = (row) => {
    setSelected(row);
    setForm({
      assigned_tutor_id: row.assigned_tutor_id ?? "",
      scheduled_date: row.scheduled_date ?? "",
      scheduled_time: row.scheduled_time ?? "",
      scheduled_weekday_id: row.scheduled_weekday_id ?? "",
      duration_minutes:
        row.duration_minutes != null ? String(row.duration_minutes) : "",
      status: row.status ?? "new",
    });
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setSelected(null);
  };

  const handleSave = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await api.put(`admin/tuition-requests/${selected.id}`, {
        assigned_tutor_id:
          form.assigned_tutor_id === "" ? null : Number(form.assigned_tutor_id),
        scheduled_date: form.scheduled_date || null,
        scheduled_time: form.scheduled_time || null,
        scheduled_weekday_id:
          form.scheduled_weekday_id === "" ? null : Number(form.scheduled_weekday_id),
        duration_minutes:
          form.duration_minutes === "" ? null : Number(form.duration_minutes),
        status: form.status,
      });
      toast.success("Tuition request updated.");
      await fetchRequests();
      closeDialog();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update tuition request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
        Tuition Requests
      </Typography>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            No tuition requests found.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8f9ff" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>School year</TableCell>
                  <TableCell>Subjects</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {r.first_name} {r.last_name}
                      </Typography>
                      {r.contact_number ? (
                        <Typography variant="caption" color="text.secondary">
                          {r.contact_number}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {r.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.school_year_name || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {(r.subjects || []).join(", ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(r.status)} variant="outlined" label={r.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.scheduled_date ? r.scheduled_date : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.scheduled_time ? r.scheduled_time : ""}{r.duration_minutes ? ` • ${r.duration_minutes} min` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button variant="outlined" size="small" onClick={() => openRequest(r)}>
                        View / Assign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open && canOpenDialog} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Tuition Request Details</DialogTitle>
        <DialogContent dividers>
          {!selected ? null : (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={800}>
                  {selected.first_name} {selected.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.email} {selected.contact_number ? `• ${selected.contact_number}` : ""}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  School year: <strong>{selected.school_year_name || "—"}</strong>
                </Typography>
                <Typography variant="body2">
                  Subjects: <strong>{(selected.subjects || []).join(", ") || "—"}</strong>
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Message
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, background: "#fafafa" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {selected.message || "—"}
                  </Typography>
                </Paper>
              </Box>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Admin Assignment
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="tutor-select-label">Assign tutor</InputLabel>
                    <Select
                      labelId="tutor-select-label"
                      value={form.assigned_tutor_id}
                      label="Assign tutor"
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, assigned_tutor_id: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>Not assigned</em>
                      </MenuItem>
                      {tutors.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.full_name || `${t.first_name || ""} ${t.last_name || ""}`.trim() || `Tutor #${t.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Schedule date (start)"
                    InputLabelProps={{ shrink: true }}
                    value={form.scheduled_date}
                    onChange={(e) => setForm((prev) => ({ ...prev, scheduled_date: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Schedule time"
                    InputLabelProps={{ shrink: true }}
                    value={form.scheduled_time}
                    onChange={(e) => setForm((prev) => ({ ...prev, scheduled_time: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="weekday-select-label">Schedule day</InputLabel>
                    <Select
                      labelId="weekday-select-label"
                      value={form.scheduled_weekday_id}
                      label="Schedule day"
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, scheduled_weekday_id: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>Not set</em>
                      </MenuItem>
                      {weekdays.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Duration (minutes)"
                    value={form.duration_minutes}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, duration_minutes: e.target.value }))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      value={form.status}
                      label="Status"
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, status: e.target.value }))
                      }
                    >
                      <MenuItem value="new">new</MenuItem>
                      <MenuItem value="in_progress">in_progress</MenuItem>
                      <MenuItem value="scheduled">scheduled</MenuItem>
                      <MenuItem value="closed">closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TuitionRequests;

